import {IconButton, InputAdornment, TextField} from "@material-ui/core";
import {LockOpen} from "@material-ui/icons";
import LockIcon from "@material-ui/icons/Lock";
import {makeStyles} from "@material-ui/styles";
import React, {InputHTMLAttributes, useState} from "react";
import {Control, useController} from "react-hook-form";

export interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  name: any;
  label?: string;
  control: Control<any>;
  borderRadius?: string;
  inputType?: string;
  startIcon?: any;
  rows?: number;
  multiline?: any;
  valueInput?: string;
  required?: boolean;
  autocomplete?: boolean;
  isShowLabel?: boolean;
  size?: "small" | "medium";
}

export default function InputField({
  name,
  label,
  control,
  borderRadius,
  inputType,
  multiline,
  startIcon,
  rows,
  valueInput,
  required,
  autocomplete,
  isShowLabel,
  size = "small",
  ...inputProps
}: InputFieldProps) {
  const useStyles = makeStyles((theme: any) => ({
    root: {
      "& >label": {
        fontSize: 16,
        padding: 8,
        paddingLeft: 6,
        color: "#999999",
        [theme.breakpoints?.down("md")]: {
          fontSize: 12,
        },
        [theme.breakpoints?.down("sm")]: {
          fontSize: 9,
          padding: 0,
        },
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: borderRadius || 10,
        backgroundColor: inputProps.disabled ? "#f8f4f4" : "#fff",
        padding: 0,
        "& .MuiOutlinedInput-inputMarginDense": {
          padding: "8.5px 14px",
          fontSize: "0.875rem",
        },
        "& >textarea": {
          padding: "8.5px 14px",
          fontSize: "0.875rem",
        },
      },
      "& .MuiFormHelperText-root.Mui-error": {
        fontSize: 10,
        padding: 0,
        margin: "0",
      },
    },
    iconLock: {
      color: "#797979",
      opacity: 0.5,
      [theme.breakpoints?.down("md")]: {
        fontSize: 18,
      },
      [theme.breakpoints?.down("sm")]: {
        fontSize: 14,
      },
    },
  }));
  const classes = useStyles();
  const {
    field: {value, onChange, onBlur, ref},
    fieldState: {error, invalid},
  } = useController({
    name,
    control,
  });
  const [values, setValues] = useState({
    value: value,
    showPassword: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => {
    setValues({...values, showPassword: !values.showPassword});
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <TextField
      autoComplete={autocomplete ? "on" : "off"}
      required={required}
      name={name}
      label={isShowLabel && label}
      placeholder={label}
      value={valueInput ? valueInput : value}
      inputRef={ref}
      onChange={onChange}
      onBlur={onBlur}
      helperText={error?.message}
      inputProps={inputProps}
      variant="outlined"
      fullWidth
      margin="dense"
      multiline={multiline}
      minRows={rows}
      error={invalid}
      size={size}
      className={classes.root}
      type={!values.showPassword && inputType === "password" ? "password" : "text"}
      InputProps={{
        style: {
          fontSize: 14,
        },

        startAdornment: startIcon,
        endAdornment: (
          <InputAdornment position="end">
            {inputType === "password" && (
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
              >
                {showPassword ? (
                  <LockOpen className={classes.iconLock} />
                ) : (
                  <LockIcon className={classes.iconLock} />
                )}
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
}
